/**
 * Client-side validation for the create-ticket forms.
 *
 * The limits are the contract's, restated here so the employee is told what is
 * wrong while they are still typing rather than by a 422 after they submit. The
 * backend validates the same rules — this is courtesy, not security.
 */

/**
 * Five, not the contract's three (7 Oct, "idiot-proof forms"): anything
 * shorter is obviously not a title IT can act on. The service request form
 * does not apply it to a subject still as the app wrote it (an offering's own
 * name may be short).
 */
export const SUBJECT_MIN_LENGTH = 5;
export const SUBJECT_MAX_LENGTH = 255;
export const DESCRIPTION_MAX_LENGTH = 4000;

/**
 * The least an incident description can be: five characters (7 Oct, plain
 * checks), above the follow-up endpoint's floor of three — "pc" or "help"
 * tells IT nothing.
 */
export const DETAILS_MIN_LENGTH = 5;

/** A follow-up answer's cap, as `TicketFollowup.answer` declares it. */
export const FOLLOWUP_ANSWER_MAX_LENGTH = 2000;

/** The closing "Anything else?" box's cap, as `additional_details` declares it. */
export const ADDITIONAL_DETAILS_MAX_LENGTH = 4000;

export type ValidationKey =
  | "subjectRequired"
  | "subjectTooShort"
  | "subjectTooLong"
  | "descriptionRequired"
  | "descriptionTooLong"
  | "detailsRequired"
  | "detailsTooShort";

/** The failing rule for a subject, or null when it passes. */
export function validateSubject(subject: string): ValidationKey | null {
  const trimmed = subject.trim();
  if (trimmed.length === 0) {
    return "subjectRequired";
  }
  if (trimmed.length < SUBJECT_MIN_LENGTH) {
    return "subjectTooShort";
  }
  if (trimmed.length > SUBJECT_MAX_LENGTH) {
    return "subjectTooLong";
  }
  return null;
}

/**
 * The failing rule for an incident's subject, which may be left blank.
 *
 * Blank is fine — the server writes one from the description — and so is
 * anything shorter than a usable title, which is treated as blank (see
 * `ownIncidentSubject`) rather than blocking Continue over "pc".
 */
export function validateOptionalSubject(subject: string): ValidationKey | null {
  return subject.trim().length > SUBJECT_MAX_LENGTH ? "subjectTooLong" : null;
}

/** The failing rule for a description, or null when it passes. */
export function validateDescription(description: string): ValidationKey | null {
  const trimmed = description.trim();
  if (trimmed.length === 0) {
    return "descriptionRequired";
  }
  if (trimmed.length > DESCRIPTION_MAX_LENGTH) {
    return "descriptionTooLong";
  }
  return null;
}

/** The failing rule for "What's happening?", the one thing an incident needs. */
export function validateIncidentDetails(details: string): ValidationKey | null {
  const trimmed = details.trim();
  if (trimmed.length === 0) {
    return "detailsRequired";
  }
  if (trimmed.length < DETAILS_MIN_LENGTH) {
    return "detailsTooShort";
  }
  if (trimmed.length > DESCRIPTION_MAX_LENGTH) {
    return "descriptionTooLong";
  }
  return null;
}
